import CloseIcon from "@mui/icons-material/Close";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
} from "@mui/material";
import { format, isBefore, startOfDay } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { getLeaveByUser, getLeaveByUserByLeaveId } from "../../../axios/User/UserApi";
import CustomInputHorizontal from "../../../components/CustomInputHorizontal";
import CustomSelect from "../../../components/CustomSelect";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import { useAuth } from "../../../context/AuthContext";
import CustomInput from "../../../components/CustomInput";
import HelpIcon from "@mui/icons-material/Help";
import { useLoading } from "../../../context/LoadingContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import WarningIcon from '@mui/icons-material/Warning';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Hiển thị số thập phân tối đa 2 chữ số, số nguyên giữ nguyên */
const formatDecimalNumber = (value) => {
  if (value == null || value === "") return 0;
  const asString = String(value).trim();
  if (asString.includes(":")) {
    const parts = asString.split(":").map((part) => Number(part) || 0);
    const [h = 0, m = 0, s = 0] = parts;
    const totalSeconds = h * 3600 + m * 60 + s;
    if (totalSeconds === 0) return 0;
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;
  if (Number.isInteger(numberValue)) return numberValue;
  return Number(numberValue.toFixed(2));
};

const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return h * 60 + m + Math.floor(s / 60);
};

const parseUsedFromCarryToDays = (value, hoursPerLeaveDay = "08:00:00") => {
  if (value == null || value === "") return 0;
  const asString = String(value).trim();
  if (asString.includes(":")) {
    const timeMins = parseTimeStringToMinutes(asString);
    const dayMins =
      parseTimeStringToMinutes(hoursPerLeaveDay) || DEFAULT_FULL_DAY_MINUTES;
    if (dayMins <= 0) return 0;
    return timeMins / dayMins;
  }
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : 0;
};

const formatLeaveBalanceDisplay = (value, hoursPerLeaveDay = "08:00:00") =>
  formatDecimalNumber(parseUsedFromCarryToDays(value, hoursPerLeaveDay));

const AddLeaveRequestDialog = (props) => {
  const {
    open,
    handleClose,
    setLoading,
    onRefresh,
    selectedDate,
    dateTimeFormat,
    leaveTypesData,
    shiftsData
  } = props;
  const { profile, isAdmin, genaralSettingTimeZone, genaralSettingDateFormat } = useAuth();
  const [show, hide] = useLoading();
  const { t } = useTranslation();
  const { addLeave, getLeaveTypeData } = LeaveApi();
  const [datepickerKey, setDatepickerKey] = useState();
  const [isManualTime, setIsManualTime] = useState(false);
  const leavePolicyData = [
    { value: 1, label: t("common.normal") },
    { value: 2, label: t("common.restricted") },
  ];
  const customStartDate = dayjs(selectedDate)
    .utcOffset(genaralSettingTimeZone, true)
    .startOf("day");

  const customEndDate = dayjs(selectedDate)
    .utcOffset(genaralSettingTimeZone, true)
    .endOf("day")
    .second(0);

  const sortedShifts = useMemo(
    () => [...shiftsData].sort((a, b) => b.shiftId - a.shiftId),
    [shiftsData]
  );

  useEffect(() => {
    if (!open) return;
    if (sortedShifts.length === 0) return;

    const firstShift = sortedShifts[0];

    setFormData((prev) => {
      if (prev.shift) return prev;

      const start = combineDateAndTime(
        prev.requestStartDate || customStartDate,
        firstShift.shiftStart
      );
      const end = combineDateAndTime(
        prev.requestEndDate || customEndDate,
        firstShift.shiftEnd
      );

      return {
        ...prev,
        shift: firstShift.shiftId,
        requestStartDate: start,
        requestEndDate: end,
      };
    });

    setDatepickerKey((k) => (k || 0) + 1);
  }, [sortedShifts, open]);

  const combineDateAndTime = (dateInput, timeStr) => {
    if (!dateInput || !timeStr) return null;

    const [hh, mm, ss] = timeStr.split(":").map(Number);

    return dayjs(dateInput)
      .tz(genaralSettingTimeZone, true)
      .hour(hh)
      .minute(mm)
      .second(ss || 0)
      .millisecond(0)
      .toDate();
  };

  const initialFormData = {
    userId: profile.userId,
    requestStartDate: customStartDate,
    requestEndDate: customEndDate,
    leaveType: leaveTypesData[0]?.leaveTypeId,
    leavePolicy: leavePolicyData[0].value,
    requestReason: "",
    username: profile.fullName,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!open) return;

    setFormData((prev) => {
      if (prev.requestStartDate && prev.requestEndDate) {
        return {
          ...prev,
          leaveType: leaveTypesData[0]?.leaveTypeId,
        };
      }

      return {
        ...prev,
        requestStartDate: customStartDate,
        requestEndDate: customEndDate,
        leaveType: leaveTypesData[0]?.leaveTypeId,
      };
    });
  }, [selectedDate, leaveTypesData, open]);

  const [, setIsSelectUserError] = useState(false);
  const [, setIsSelectValid] = useState(false);
  const [, setIsInputValid] = useState(false);
  const [, setResetTrigger] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [validationMessages, setValidationMessages] = useState({
    userId: "",
    requestEndDate: "",
  });
  const [, setIsError] = useState({
    requestEndDate: false,
  });

  const toUTCByBrowserTimezone = (date) => {
    if (!date) return null;

    const browserTZ = dayjs.tz.guess();

    return dayjs(date)
      .tz(browserTZ, true) // giữ nguyên giờ user chọn
      .utc()
      .format(); // ISO UTC Z
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // setLoading(true);
    show();

    let submitData = { ...formData };

    submitData.requestStartDate = toUTCByBrowserTimezone(
      submitData.requestStartDate
    );

    submitData.requestEndDate = toUTCByBrowserTimezone(
      submitData.requestEndDate
    );

    submitData.leaveType = formData.leaveType;
    submitData.langId = localStorage.getItem("i18nextLngId");
    submitData.leavePolicy = isAdmin ? formData.leavePolicy : "";
    try {
      const response = await addLeave(submitData);
      if (response.data.responseCode === 0) {
        setLoading(false);
        handleCloseDialog();
        toast.success(t("common.add-leave-request-successfully"));
        onRefresh();
        setTimeout(() => {
          hide();
        }, 4000);
      } else if (response.data.responseCode === 1085) {
        setLoading(false);
        handleCloseDialog();
        // toast.warning(response.data.message);
        toast.success(t("common.add-leave-request-successfully"));
        onRefresh();
        setTimeout(() => {
          hide();
        }, 4000);
      } else {
        toast.error(response.data.message);
        hide();
      }
    } catch (error) {
      if (error.response.data.responseCode === 1085) {
        handleCloseDialog();
        // toast.warn(error.response.data.message);
        toast.success(t("common.add-leave-request-successfully"));
        onRefresh();
        setTimeout(() => {
          hide();
        }, 4000);
      } else {
        hide();
        toast.error(error.response.data.message);
      }
    }
  };

  const applyShiftToDate = (baseDate, shift) => {
    if (!shift) {
      return {
        start: combineDateAndTime(baseDate, "00:00:00"),
        end: combineDateAndTime(baseDate, "23:59:00"),
      };
    }

    const start = combineDateAndTime(baseDate, shift.shiftStart);

    let end = combineDateAndTime(baseDate, shift.shiftEnd);

    // ✅ ca qua ngày (22:00 → 00:00)
    if (shift.shiftEnd === "00:00:00" || isBefore(end, start)) {
      end = dayjs(end).add(1, "day").toDate();
    }

    return { start, end };
  };


  const handleDateChange = (startDate, endDate) => {
    const isHour = formData.leaveType === 1;

    if (isHour) {
      // ✅ leave theo giờ → giữ nguyên giờ user chọn
      setIsManualTime(true);

      setFormData((prev) => ({
        ...prev,
        requestStartDate: toDate(startDate),
        requestEndDate: toDate(endDate),
      }));
    } else {
      // ✅ leave theo ngày → ép full day
      const start = dayjs(toDate(startDate))
        .tz(genaralSettingTimeZone, true)
        .startOf("day")
        .toDate();

      const end = dayjs(toDate(endDate))
        .tz(genaralSettingTimeZone, true)
        .endOf("day")
        .toDate();

      setFormData((prev) => ({
        ...prev,
        requestStartDate: start,
        requestEndDate: end,
      }));
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setFormData(initialFormData);
    setIsSelectUserError(false);
    setHasUserInteracted(false);
    setSelectedValue(null);
    setResetTrigger((prev) => !prev);
    setFormData({
      ...initialFormData,
      normalDateFrom: selectedDate,
      normalDateTo: selectedDate,
      startFrom: selectedDate,
      endsOn: selectedDate,
    });
    setDatepickerKey();
  };

  const toDate = (v) => {
    if (!v) return new Date(NaN);
    if (dayjs.isDayjs(v)) return v.toDate();
    if (v instanceof Date) return v;
    return new Date(v); // fallback cho string
  };

  const setEndOfDay = (val) => {
    const d = toDate(val);
    if (isNaN(d.getTime())) return d;
    d.setHours(23, 59, 0, 0); // 23:59:00
    return d;
  };

  const MAX_REASON_LENGTH = 255;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "requestReason" && value.length > MAX_REASON_LENGTH) {
      return;
    }

    // chọn ca
    if (name === "shift") {
      const selectedShift = sortedShifts.find(
        (s) => String(s.shiftId) === String(value)
      );

      setFormData((prev) => {
        if (!selectedShift) return prev;

        if (isManualTime) {
          // ❗ user đã chỉnh giờ → KHÔNG overwrite
          return { ...prev, shift: selectedShift.shiftId };
        }

        const baseDate = startOfDay(
          toDate(prev.requestStartDate || selectedDate)
        );

        const { start, end } = applyShiftToDate(baseDate, selectedShift);

        return {
          ...prev,
          shift: selectedShift.shiftId,
          requestStartDate: start,
          requestEndDate: end,
        };
      });

      return;
    }

    if (name === "leaveType") {
      const valueNum = Number(value);
      const isHour = valueNum === 1;

      setIsManualTime(false);

      setFormData((prev) => {
        let start = toDate(prev.requestStartDate);
        let end = toDate(prev.requestEndDate);
        let shiftId = prev.shift;

        if (isHour) {
          // 🔥 Nếu quay lại leave theo giờ → auto fill shift đầu tiên
          if (sortedShifts.length) {
            const firstShift = sortedShifts[0];

            const baseDate = startOfDay(start || selectedDate);

            const { start: newStart, end: newEnd } =
              applyShiftToDate(baseDate, firstShift);

            start = newStart;
            end = newEnd;
            shiftId = firstShift.shiftId;
          }
        } else {
          // leave theo ngày
          start = dayjs(start)
            .tz(genaralSettingTimeZone, true)
            .startOf("day")
            .toDate();

          end = dayjs(end)
            .tz(genaralSettingTimeZone, true)
            .endOf("day")
            .toDate();

          shiftId = null;
        }

        return {
          ...prev,
          leaveType: valueNum,
          requestStartDate: start,
          requestEndDate: end,
          shift: shiftId,
        };
      });

      return;
    }

    // các field khác: leaveType, leavePolicy, requestReason, responseReason...
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!open) return;
    if (sortedShifts.length === 0) return;

    const firstShift = sortedShifts[0];

    const baseDate = dayjs(selectedDate).tz(genaralSettingTimeZone).startOf('day');

    const start = combineDateAndTime(baseDate, firstShift.shiftStart);
    const end = combineDateAndTime(baseDate, firstShift.shiftEnd);

    setFormData(prev => ({
      ...prev,
      shift: firstShift.shiftId,
      requestStartDate: start,
      requestEndDate: end,
    }));

    setDatepickerKey(k => (k || 0) + 1);
  }, [sortedShifts, open, selectedDate, genaralSettingTimeZone]);

  const [, setSelectedValue] = useState(null);

  const handleDataChange = (name, newData) => {
    if (newData && newData.userId) {
      setHasUserInteracted(true);
      setSelectedValue(newData);
      setFormData((prevData) => ({
        ...prevData,
        [name]: newData.userId,
      }));
    } else {
      setSelectedValue(newData);
      setFormData((prevData) => ({
        ...prevData,
        [name]: newData,
      }));
    }
  };

  useEffect(() => {
    if (hasUserInteracted) {
      if (formData.userId === "") {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          userId: "User is required",
        }));
      } else {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          userId: "",
        }));
      }
    }
  }, [formData.userId, hasUserInteracted]);

  useEffect(() => {
    if (hasUserInteracted) {
      const { userId } = formData;
      const isUserIdValid = userId !== "" && validationMessages.userId === "";
      isUserIdValid ? setIsSelectUserError(null) : setIsSelectUserError(true);
      if (isUserIdValid) {
        setIsSelectValid(true);
      } else {
        setIsSelectValid(false);
      }
    }
  }, [formData.userId, validationMessages.userId, hasUserInteracted]);

  useEffect(() => {
    const { requestEndDate } = formData;
    const isEndDateValid =
      requestEndDate && validationMessages.requestEndDate === "";
    setIsError((prevError) => ({
      ...prevError,
      requestEndDate: isEndDateValid ? null : true,
    }));
    if (isEndDateValid) {
      setIsInputValid(true);
    } else {
      setIsInputValid(false);
    }
  }, [formData.requestEndDate, validationMessages.requestEndDate]);

  const [checkRequestedTo, setCheckRequestedTo] = useState(false);
  const [checkRequestedFrom, setCheckRequestedFrom] = useState(false);

  useEffect(() => {
    setCheckRequestedTo(formData.requestEndDate);
    setCheckRequestedFrom(formData.requestStartDate);

  }, [formData.requestEndDate, formData.requestStartDate]);


  const [leaveByUser, setLeaveByUserData] = useState([]);
  const [leaveByUserByLeave, setLeaveByUserByLeaveData] = useState([]);

  const usedFromYear =
    leaveByUser.find(item => item.leaveTypeId === 1)?.usedFromYear || "00:00:00";

  const fetchLeaveByUserData = async (userId) => {
    if (!userId) return;

    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId: userId,
      year: new Date().getFullYear()
    };

    try {
      const response = await getLeaveByUser(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveByUserData(response.data.data ?? []);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(error?.response?.data?.message || "Error");
      } else {
        toast.error(t('common.network-timeout'));
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!formData.userId) return;

    fetchLeaveByUserData(formData.userId);
  }, [formData.userId, open]);

  const fetchLeaveByUserByLeaveId = async (userId, leaveTypeId) => {
    if (!userId || !leaveTypeId) return;

    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId: userId,
      leaveTypeId: leaveTypeId,
      // year: new Date().getFullYear()
      year: toDate(formData.requestStartDate).getFullYear()
    };

    try {
      const response = await getLeaveByUserByLeaveId(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveByUserByLeaveData(response.data.data ?? null);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(error?.response?.data?.message || "Error");
      } else {
        toast.error(t('common.network-timeout'));
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!formData.userId || !formData.leaveType) return;

    fetchLeaveByUserByLeaveId(
      formData.userId,
      formData.leaveType
    );
  }, [formData.userId, formData.leaveType, open, formData.requestStartDate]);

  useEffect(() => {
    setLeaveByUserData([]);
    setLeaveByUserByLeaveData([]);
  }, [formData.userId]);

  const leaveEntitlementText = useMemo(() => {
    if (!leaveByUserByLeave?.length) return "—"; // hoặc ""
    const { totalEntitlementYear, usedFromYear } = leaveByUserByLeave[0];
    return `${formatDecimalNumber(usedFromYear)}/${formatDecimalNumber(totalEntitlementYear)}`;
  }, [leaveByUserByLeave]);

  const carryLeaveText = useMemo(() => {
  if (!leaveByUserByLeave?.length) return null;

  const item = leaveByUserByLeave[0];
  if (!item.isCarry) return null;

  if (item.expiredDt) {
    const now = new Date();
    const expiredDate = new Date(item.expiredDt);

    // ✅ bỏ giờ
    now.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);

    if (now > expiredDate) {
      return null;
    }
  }

  const expired = item.expiredDt
    ? format(new Date(item.expiredDt), genaralSettingDateFormat)
    : "";
  const hoursPerDay =
    item?.hoursPerLeaveDaySnapshot || item?.hoursPerLeaveDay || "08:00:00";

  return `${formatLeaveBalanceDisplay(item.usedFromCarry, hoursPerDay)}/${formatLeaveBalanceDisplay(item.carryIn, hoursPerDay)} (${t("common.expire-on")} ${expired})`;
}, [leaveByUserByLeave, t, genaralSettingDateFormat]);


  const requestedDays = useMemo(() => {
    if (!formData.requestStartDate || !formData.requestEndDate) return 0;

    const start = startOfDay(toDate(formData.requestStartDate));
    const end = startOfDay(toDate(formData.requestEndDate));

    const diff =
      Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    return diff > 0 ? diff : 0;
  }, [formData.requestStartDate, formData.requestEndDate]);

  const isLeaveByHour = formData.leaveType === 1;

  const isOverEntitlement = useMemo(() => {
    if (formData.leaveType === 1) return false; // leave theo giờ bỏ qua

    if (!leaveByUserByLeave?.length) return false;

    const item = leaveByUserByLeave[0];

    const totalEntitlementYear = Number(item.totalEntitlementYear) || 0;
    const usedFromYear = Number(item.usedFromYear) || 0;

    const isCarry = item.isCarry;
    const carryIn = Number(item.carryIn) || 0;
    const usedFromCarry = Number(item.usedFromCarry) || 0;

    let carryAvailable = 0;

    // ✅ check carry còn hạn
    if (isCarry && item.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(item.expiredDt);

      if (now <= expiredDate) {
        carryAvailable = Math.max(carryIn - usedFromCarry, 0);
      }
    }

    const totalAvailable =
      (totalEntitlementYear - usedFromYear) + carryAvailable;

    return requestedDays > totalAvailable;
  }, [leaveByUserByLeave, requestedDays, formData.leaveType]);

  const shiftRange = useMemo(() => {
    if (formData.leaveType !== 1) return null;
    if (!formData.shift) return null;

    const shift = sortedShifts.find(
      (s) => String(s.shiftId) === String(formData.shift)
    );

    if (!shift) return null;

    const baseDate = startOfDay(
      toDate(formData.requestStartDate || selectedDate)
    );

    const { start, end } = applyShiftToDate(baseDate, shift);

    return { start, end };
  }, [formData.shift, formData.leaveType, formData.requestStartDate, sortedShifts]);

  return (
    <CustomDialog
      maxWidth={"sm"}
      open={open}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">
              {t("common.add-leave-request")}
            </p>
          </div>
          <GrayIconButton onClick={handleCloseDialog} className="self-start">
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {/* <p className="pt-6 text-sm text-[#DC2626]">
          {t("common.required-fields-are-marked-in-red")} "*".
        </p> */}
        <div className="pt-[10px] grid grid-cols-6 items-center">
          <div className="col-span-6 md:col-span-2">
            <p>
              {t("common.leave-staff")}{" "}
            </p>
          </div>
          <div className="col-span-6 md:col-span-4 pt-2 md:pt-0">
            <div className="w-full">
              <CustomInput
                name="username"
                inputType="text"
                readOnly={true}
                value={formData.username}
                handleInputChange={handleInputChange}
                labelEnable={false}
                placeholder={t("common.userName")}
              />
            </div>
          </div>
        </div>
        <div className="pt-5 grid grid-cols-6 items-center">
          <div className="col-span-6 md:col-span-2">
            <p>{t("common.leave-type")}{" "}
              <span className="text-[#DC2626]">*</span></p>
          </div>
          <div className="col-span-6 md:col-span-4 pt-2 md:pt-0">
            <FormControl className="w-full">
              <CustomSelect
                name="leaveType"
                id="addLeaveType"
                value={formData.leaveType}
                onChange={handleInputChange}
                options={leaveTypesData}
                dataType="leaveType"
              />
            </FormControl>
          </div>
        </div>
        {isLeaveByHour && (
          <div className="pt-5 grid grid-cols-6 items-center">
            <div className="col-span-6 md:col-span-2">
              <p>
                {t("common.shifts")}{" "}
                <span className="text-[#DC2626]">*</span>
              </p>
            </div>
            <div className="col-span-6 md:col-span-4 pt-2 md:pt-0">
              <FormControl className="w-full">
                <CustomSelect
                  name="shift"
                  id="addShift"
                  value={formData.shift}
                  onChange={handleInputChange}
                  options={sortedShifts}
                  dataType="shift"
                />
              </FormControl>
            </div>
          </div>
        )}
        <div className="pt-5 grid grid-cols-6 items-center">
          <div className="col-span-6 md:col-span-2">
            <p>{t("common.leave-time")}{" "}
              <span className="text-[#DC2626]">*</span></p>
          </div>

          <div className="col-span-6 md:col-span-4 pt-2 md:pt-0">
            <CustomDateTimeRangePicker
              key={datepickerKey}
              value={[
                formData.requestStartDate,
                formData.requestEndDate,
              ]}
              disableTime={!isLeaveByHour}
              minDateTime={shiftRange?.start}
              maxDateTime={shiftRange?.end}
              onChange={([start, end]) => {
                if (!start || !end) return;

                handleDateChange(start, end);
              }}
              dateTimeFormat={dateTimeFormat}
            />
          </div>
        </div>


        {formData.leaveType !== 1 && (
          <div className="pt-2 grid grid-cols-6 items-center">
            <div className="col-span-6">
              <CustomInputHorizontal
                required={false}
                title={t("common.days-off")}
                inputType="text"
                name="leaveEntitlement"
                value={leaveEntitlementText}
                readOnly
              />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="pt-2 grid grid-cols-6 items-center">
            <div className="col-span-6 md:col-span-2"></div>
            <div className="col-span-6  md:col-span-4">
              <div className="pt-2 flex items-center gap-2">
                <div className="self-start">
                  <HelpIcon sx={{ color: "#3B82F6" }} />
                </div>
                <p className="text-[#3B82F6]">
                  {t("common.you-can-add-new-leave-types-from-setting-icon")}.
                </p>
              </div>
            </div>
          </div>
        )}

        {formData.leaveType !== 1 && carryLeaveText && (
          <div className="pt-2 grid grid-cols-6 items-center">
            <div className="col-span-6">
              <CustomInputHorizontal
                required={false}
                title={t("common.carry-leave", {
                  year: new Date().getFullYear() - 1
                })}
                inputType="text"
                value={carryLeaveText}
                readOnly
              />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="pt-5 grid grid-cols-6 items-center">
            <div className="col-span-6 md:col-span-2">
              <p>{t("common.leave-policy")}{" "}
                <span className="text-[#DC2626]">*</span></p>
            </div>
            <div className="col-span-6 md:col-span-4 pt-2 md:pt-0">
              <FormControl className="w-full">
                <CustomSelect
                  name="leavePolicy"
                  id="addLeavePolicy"
                  value={formData.leavePolicy}
                  onChange={handleInputChange}
                  options={leavePolicyData}
                />
              </FormControl>
            </div>
          </div>
        )}

        <div className="grid grid-cols-6 items-center mt-3">
          <div className="col-span-6">
            <CustomInputHorizontal
              required={true}
              title={
                <>
                  {t("common.leave-request")}
                </>
              }
              inputType="text"
              name="requestReason"
              placeholder={t("common.leave-request")}
              handleInputChange={handleInputChange}
              value={formData.requestReason}
              className="h-10"
            />

            <div className="flex justify-end mt-1 text-xs">
              <span
                className={
                  formData.requestReason.length >= 255
                    ? "text-red-500"
                    : "text-gray-400"
                }
              >
                {formData.requestReason.length} / 255
              </span>
            </div>
          </div>
        </div>
        {isOverEntitlement && formData.leaveType !== 1 && (
          <div className="pt-4">
            <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-md text-sm flex gap-2 items-center">
              <WarningIcon className="inline mr-2 w-2 h-2" />
              <div>{t("common.overEntitlementWarning")}</div>
            </div>
          </div>
        )}

      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={
              !formData.requestReason ||
              (checkRequestedTo &&
                checkRequestedFrom &&
                checkRequestedTo < checkRequestedFrom)
            }
          >
            <p>{t("button.send-request")}</p>
          </ControlpanelPrimaryButton>

        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddLeaveRequestDialog;
